import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IMetaDetails } from "store/types";

interface Props {
  selectedItems?: IMetaDetails[];
  onClose?: () => void;
  onDownload?: () => void;
  onChangeClassification?: (anchorEl: HTMLElement | null) => void;
  onChangeStatus?: (anchorEl: HTMLElement | null) => void;
  onDelete?: () => void;
}

const iconButtonSx = {
  p: "6px",
  "&:hover": { backgroundColor: "#587189" }
};

export const BulkActionsBar: React.FC<Props> = ({
  selectedItems = [],
  onClose,
  onDownload,
  onChangeClassification,
  onChangeStatus,
  onDelete
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  if (!selectedItems.length) return null;

  const allFiles = selectedItems.every((item) => item.entity_type === "file");
  const hasFiles = selectedItems.some((item) => item.entity_type === "file");

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="12px"
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#496682",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "8px",
        padding: "12px 20px",
        zIndex: 1000
      }}
    >
      {onClose && (
        <MaterialTooltip title="Clear selection">
          <IconButton onClick={onClose} sx={{ ...iconButtonSx, color: "white" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </MaterialTooltip>
      )}

      <Typography sx={{ fontSize: 14, color: "white", fontWeight: 500, whiteSpace: "nowrap" }}>
        {selectedItems.length} {selectedItems.length === 1 ? "item" : "items"} selected:
      </Typography>

      <Stack direction="row" alignItems="center" gap="4px">
        {hasFiles && onChangeClassification && (
          <MaterialTooltip title="Change Classification" isShow>
            <IconButton onClick={(e) => onChangeClassification(e.currentTarget)} sx={iconButtonSx}>
              <LabelOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {hasFiles && onChangeStatus && (
          <MaterialTooltip title="Change Status" isShow>
            <IconButton onClick={(e) => onChangeStatus(e.currentTarget)} sx={iconButtonSx}>
              <FlagOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {allFiles && onDownload && (
          <MaterialTooltip title="Download" isShow>
            <IconButton onClick={onDownload} sx={iconButtonSx}>
              <FileDownloadOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {onDelete && (
          <MaterialTooltip title="More Actions" isShow>
            <IconButton onClick={handleMenuClick} sx={iconButtonSx}>
              <MoreVertIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                borderRadius: "8px",
                minWidth: 220
              }
            }
          }}
        >
          {onDelete && (
            <MaterialTooltip title="Delete" placement="left">
              <MenuItem onClick={() => handleMenuAction(onDelete)}>
                <ListItemIcon>
                  <DeleteOutlinedIcon sx={{ fontSize: 20, color: "#496682" }} />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </MaterialTooltip>
          )}
        </Menu>
      </Stack>
    </Stack>
  );
};
